import { T_MongoDoc, T_MongoId } from "../types/_utils.types";
import { BusinessInfo, ClientDetails } from "./application.dto";

type ISODate = string;
type ISOMonth = string;

export interface MonthlyRevenue {
  month: ISOMonth;
  /** Gross receivables recognised for the period covered. */
  revenue: number;
  /** True when this figure covers only part of the calendar month. */
  partial?: boolean;
  /** Last day covered. Required when `partial` is true. */
  coveredThrough?: ISODate;
}

export interface McaAdvance {
  fundedDate: ISODate;
  fundedAmount: number;
  /** Total payback / RTR. What the funder is entitled to collect. */
  paybackAmount: number;
  weeklyPayment: number;
  /** Specified percentage of receivables. Accepts 0.12 or 12. */
  specifiedPercentage: number;
  /** Outstanding balance as of `lastPaidDate` (or `asOf` when not supplied). */
  currentBalance?: number;
  /** Last date a payment is known to have cleared. Closes the window. */
  lastPaidDate?: ISODate;
  /** Override. Ignored when `currentBalance` is supplied. */
  missedInstallments?: number;
}

/**
 * How each month's revenue is weighted into the window.
 * @default MonthWeighting.PARTIAL
 */
export enum MonthWeighting {
  /**
   * Full calendar months inside the window contribute 100% of pct * revenue.
   * Only partial months (the funding month and the trailing month) convert via weeks / 4.33. DEFAULT.
   */
  PARTIAL = "partial433",
  /**
   * Every month weighted by daysInWindow / daysInMonth.
   * Most precise, no drift, easiest to defend line by line.
   */
  CALENDAR = "calendarDays",
  /**
   * Every month weighted by (daysInWindow / 7) / 4.33.
   * Reproduces a flat spreadsheet.
   * Note this underweights February by ~8% and overweights 31-day months by ~2%.
   */
  STRICT = "strict433",
}

/**
 * @default Estimator.MEAN
 */
export enum Estimator {
  MEAN = "mean",
  MEDIAN = "median",
  NEAREST = "nearest",
  TRAILING = "trailing",
}

export interface CalcOptions {
  /** Evaluation date. Defaults to today (UTC). */
  asOf?: ISODate;
  /** Days between funding and the first debit. Default 7. */
  firstPaymentOffsetDays?: number;
  /** Month-to-week divisor. Default 4.33. */
  weeksPerMonth?: number;
  /** How partial and full months are weighted. Default 'partial433'. */
  monthWeighting?: MonthWeighting;
  /** How months with no revenue data are filled. Default 'mean'. */
  estimator?: Estimator;
  /** Window size for the 'trailing' estimator. Default 3. */
  trailingN?: number;
  /** Stop accruing expected once it reaches payback. Default true. */
  capExpectedAtPayback?: boolean;
}

export enum RevenueSource {
  ACTUAL = "actual",
  ACTUAL_PARTIAL = "actual-partial",
  INTERPOLATED = "interpolated",
  ESTIMATED = "estimated",
}

export interface MonthBreakdown {
  month: ISOMonth;
  /** Full-calendar-month equivalent revenue used in the calculation. */
  monthlyRevenue: number;
  revenueSource: RevenueSource;
  daysInWindow: number;
  weeksInWindow: number;
  /** Fraction of a full month applied to this month's revenue. */
  monthWeight: number;
  expected: number;
  cumulativeExpected: number;
}

export enum WindowEndReason {
  LAST_PAID_DATE = "lastPaidDate",
  AS_OF = "asOf",
  DERIVED_PAYOFF = "derivedPayoff",
}

export enum PaymentBasis {
  BALANCE = "balance",
  MISSED_INSTALLMENTS = "missedInstallments",
  ASSUMED_FULL = "assumedFull",
}

export enum Confidence {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

export enum Direction {
  OVERPAID = "overpaid",
  UNDERPAID = "underpaid",
  EVEN = "even",
}

export type McaExpectedPaymentResult = {
  window: {
    fundedDate: ISODate;
    /** Revenue accrual starts the day after funding. */
    accrualStart: ISODate;
    end: ISODate;
    endReason: WindowEndReason;
    days: number;
    weeks: number;
    installmentsElapsed: number;
  };
  expected: {
    total: number;
    cappedAtPayback: boolean;
    /** Date cumulative expected crosses payback, when it does. */
    reconciledPayoffDate: ISODate | null;
    /** expected / installmentsElapsed. The weekly debit the % implies. */
    impliedWeeklyPayment: number;
    breakdown: MonthBreakdown[];
  };
  actual: {
    total: number;
    basis: PaymentBasis;
    installmentsPaid: number;
    installmentsMissed: number;
    /** payback - actual. Echoed back or derived, depending on basis. */
    balance: number;
  };
  variance: {
    amount: number;
    percentOfExpected: number | null;
    direction: Direction;
    /** Variance expressed in weeks of the contractual debit. */
    weeksOfDebit: number;
  };
  coverage: {
    monthsInWindow: number;
    monthsWithActualRevenue: number;
    /** Share of `expected.total` derived from real revenue, 0..1. */
    actualRevenueShare: number;
    historyVisibleFromFundedDate: boolean;
    continuous: boolean;
    confidence: Confidence;
  };
  flags: string[];
};

export enum LOAN_TYPES {
  MCA = "mca",
  LOAN = "bank loan",
  CREDIT_CARD = "credit card",
  OTHERS = "others",
}

export enum OCR_STATUS {
  PENDING = "pending", // in process
  RUNNING = "running",
  FAILED = "failed", // retrieval failed
  COMPLETED = "completed", // captured
  UPSTREAM = "upstream",
  STOPPED = "stopped",
  MODIFIED = "modified",
  NONE = "none",
}

export enum FREQUENCY_TYPES {
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  DAILY = "daily",
  YEARLY = "yearly",
}

export enum CreditorMatchSync {
  PROBABLE = "probable",
  DRAFT = "draft",
  MATCHED = "matched",
}

type MonthlyMCABreakdown = {
  month: string; // "YYYY-MM"
  month_label: string; // "October 2024"
  true_revenue: number;
  mca_amount: number;
  is_extrapolated: boolean;
};

type McaFields = {
  month_range_start: string; // first month included
  month_range_end: string; // last month included
  monthly_breakdown: MonthlyMCABreakdown[];
  total_mca_amount: number;
  expected_payment_breakdown: McaExpectedPaymentResult;
};

export type LoanDto = {
  user: T_MongoId;
  loan_type: LOAN_TYPES;
  loan_number?: string;
  funded_amount?: number;
  funded_date?: string;
  payback_amount?: number;
  currency: string;
  payments?: number;
  frequency?: FREQUENCY_TYPES;
  due_amount?: number;
  apr?: number;
  irr?: number;
  attachments: T_MongoId[];
  creditor_name: string;
  tenure: number;
  ocrState?: OCR_STATUS;
  client_details?: ClientDetails;
  business_info?: BusinessInfo;
  creditor_id?: string;
  creditor_match?: CreditorMatchSync;
  receivables?: number;
  partner_creditor_id?: string;
  confidence_score?: number;
  ocr_remark?: string;
  net_purchase_price?: number;
  origination_fees?: number;
  potential_refinance?: boolean;
  breakeven_amount?: number;
  balance_to_totaldebt_percentage?: number;
  additional_fees_deductions?: number;
  net_funded_amount?: number;
} & Partial<McaFields>;

export type LoanSchemaDto = LoanDto & T_MongoDoc;
export type CreateLoanDto = LoanSchemaDto;
export type UpdateLoanDto = Partial<LoanSchemaDto>;
